// Monthly withholding remittance to SAR (#4489) — mirrors Finance.API WithholdingRemittance* DTOs.
import { ApDocumentType, Currency, EntityKey } from '../ap-document/ap-document';

/** Draft = being prepared; Posted = paid to SAR. Mirrors Finance.Models ApPaymentStatus. */
export enum WithholdingRemittanceStatus {
  Draft = 1,
  Posted = 2,
}

/** ISR (income tax / DMR) vs IVA (value-added / DEI-352). Mirrors WithholdingTaxKind. The isv* field
 *  names below are internal and deliberately unchanged — #4782 was a display-only rename. */
export enum WithholdingTaxKind {
  IncomeTax = 1,
  ValueAdded = 2,
}

export interface WithholdingRemittanceLine {
  retentionReference: string;
  retentionNumber: string;
  invoiceReference?: string | null;
  invoiceNumber: string;
  providerName: string;
  amount: number;
  kind: WithholdingTaxKind;
  /** The Cash Request that PLANNED this retention (#4796) — derived from the retention, not typed. Null when
   *  it was planned nowhere or in more than one week; posting then falls back to the remittance's own. */
  cashRequest?: EntityKey | null;
}

/**
 * A charge SAR added on top of the withholdings (#5029) — late penalty, surcharge, interest.
 *
 * Not a remittance line: it clears no liability (nothing was ever withheld for it) and is counted in
 * neither the ISR nor the IVA split, which are what get filed as DMR / DEI-352.
 */
export interface WithholdingRemittanceCharge {
  id: string;
  description: string;
  glAccountReference?: string | null;
  glAccountNumber?: string | null;
  accountName?: string | null;
  amount: number;
}

export interface WithholdingRemittance {
  id?: string | null;
  number: string;
  periodYear: number;
  periodMonth: number;
  dueDate: string;
  financialCompanyReference: string;
  amount: number;
  isrAmount: number;
  isvAmount: number;
  currency: Currency;
  /** The currency of the account the cash left (#5029). Differs from `currency` only when a Lempira filing
   *  was funded from a foreign account and the bank converted on the way out. */
  fundingCurrency?: Currency | null;
  /** The rate the bank gave for that wire, non-USD side per USD. Null when nothing converted. */
  fundingRateToUsd?: number | null;
  /** What left the funding account, in `fundingCurrency`. Null when nothing converted — `totalPaid` is
   *  already that figure then. */
  fundingAmount?: number | null;
  bankAccountReference?: string | null;
  bankGlAccountReference?: string | null;
  bankGlAccountNumber?: string | null;
  journalEntryReference?: string | null;
  journalEntryDocumentNumber?: string | null;
  bankTransactionReference?: string | null;
  bankTransactionNumber?: string | null;
  bankingReference?: string | null;
  lines: WithholdingRemittanceLine[];
  /** What SAR charged on top (#5029). Free description, officer-chosen GL account. */
  charges: WithholdingRemittanceCharge[];
  /** Total of `charges`. */
  chargesAmount: number;
  /** The cash that leaves the bank: `amount` (the liability cleared) + `chargesAmount`. */
  totalPaid: number;
  status: WithholdingRemittanceStatus;
  notes?: string | null;
  /** The Cash Request this money comes from (#4796) — pre-filled from the plan when every planned line agrees
   *  on one week, otherwise the officer's pick at post time. */
  cashRequest?: EntityKey | null;
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

/** One AP document row on the remittance worksheet ("here's what you're about to pay"). */
export interface RemittanceWorksheetRow {
  documentReference: string;
  documentNumber: string;
  /** The provider's fiscal (CAI) number (#4893) — the worksheet is checked against the month's paper
   *  invoices before the remittance is filed, and only this number appears on them. */
  fiscalNumber?: string | null;
  documentType: ApDocumentType;
  providerName: string;
  postingDate: string;
  documentTotal: number;
  /** None (no certificate — warning) / Draft / Posted. */
  retentionStatus: 'None' | 'Draft' | 'Posted';
  retentionReference?: string | null;
  retentionNumber?: string | null;
  withheldAmount: number;
  remitted: boolean;
  remittedDate?: string | null;
}

/**
 * One filing already made for a period (#5029) — a row in "this is what you remitted".
 *
 * A summary, not the whole remittance: the list renders on every worksheet load and a month's filing can
 * carry hundreds of retention lines. Opening a row fetches the full document by id.
 */
export interface RemittanceSummary {
  id?: string | null;
  number: string;
  status: WithholdingRemittanceStatus;
  currency: Currency;
  /** The withholdings remitted — the liability cleared. */
  amount: number;
  /** What SAR charged on top. */
  chargesAmount: number;
  /** The cash that left the bank. */
  totalPaid: number;
  /** How many retentions this filing covered — tells a full month from a late top-up without opening it. */
  lineCount: number;
  postedDate?: string | null;
  journalEntryDocumentNumber?: string | null;
}

export interface RemittanceWorksheet {
  periodYear: number;
  periodMonth: number;
  dueDate: string;
  totalToRemit: number;
  isrTotal: number;
  isvTotal: number;
  /** Count of period documents with no withholding certificate — surfaced as a soft warning. */
  missingWithholdingCount: number;
  /** A posted remittance already exists for this period (its id), else null. With multiple filings allowed
   *  this names the LATEST posted one. */
  existingRemittanceReference?: string | null;
  /** Whether another remittance may be opened for this period (#5029) — the API's answer, never derived
   *  here. False either because a draft is still open or because the deployment allows one filing per
   *  period; the attempt explains which. */
  canRemit: boolean;
  /** Every filing already made for this period, oldest first (#5029) — what a late top-up is added on top
   *  of. Replaces reading `existingRemittanceReference` as "the" remittance, which could name only one. */
  remittances: RemittanceSummary[];
  rows: RemittanceWorksheetRow[];
}

/** Post payload — mirrors Finance.API WithholdingRemittancePostConfirm. */
export interface WithholdingRemittancePostConfirm {
  bankAccountReference?: string | null;
  bankTransactionNumber?: string | null;
  /** The rate the bank gave for the wire, non-USD side per USD (#5029). Required when the chosen account
   *  holds a different money than the remittance owes, ignored otherwise — the API demands it rather than
   *  defaulting from the rate table, because the bank charges its own spread and only the person who asked
   *  for the wire knows what it cost. */
  exchangeRateToUsd?: number | null;
  /** Which Cash Request the money comes from (#4796). Optional — a tag, never a gate: it is stamped onto
   *  every line the plan did not already account for, and lines with their own tag keep it. */
  cashRequestReference?: string | null;
  notes?: string | null;
}

/** Charge save payload — mirrors Finance.API WithholdingRemittanceChargesCommand. Replaces the whole list. */
export interface WithholdingRemittanceChargesCommand {
  charges: WithholdingRemittanceChargeCommand[];
}

export interface WithholdingRemittanceChargeCommand {
  description: string | null;
  glAccountReference: string | null;
  amount: number;
}
